import React, { useMemo, useContext } from 'react';
import { Box } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import { neutral } from '_lex/_foundations/phxColors';
import { TabItem, TabsProps } from '_lex/_atoms/Tabs';
import Training from './Training';
import Policy from './Policy';
import {
  ReportingPeriodPicker,
  getTimeFilterOptions,
  getDateFilterValue,
  PeriodOption,
} from '../components/ReportingPeriodPicker';
import { SingleValue } from 'react-select';
import { useUrlStates } from 'hooks/useUrlState';
import { ReportingPeriodFilter } from './Training/_types';
import { AppContext } from '../../../context';
import { isFeatureEnabled } from 'data_sources/constants';

const InsightsCenterContent: React.FC = () => {
  const { hasPermission, currentOrganizationId } = useContext(AppContext);

  // Determine available tabs based on permissions
  const availableTabs = useMemo(() => {
    const tabs: TabItem[] = [];

    // Show policy tab only if user has accessPolicyInsightsCenter permission
    if (
      hasPermission('accessPolicyInsightsCenter', currentOrganizationId || '') &&
      isFeatureEnabled('policy_insights_center')
    ) {
      tabs.push({
        label: t('reporting.insightsCenter.tabs.policy'),
        value: 'policy',
      });
    }

    // Show training tab only if user has accessTrainingInsightsCenter permission
    if (hasPermission('accessTrainingInsightsCenter', currentOrganizationId || '')) {
      tabs.push({
        label: t('reporting.insightsCenter.tabs.training'),
        value: 'training',
      });
    }

    return tabs;
  }, [hasPermission, currentOrganizationId]);

  // Set default tab to first available tab
  const defaultTab = availableTabs.length > 0 ? availableTabs[0].value : 'policy';

  const { values, setters } = useUrlStates({
    tab: { defaultValue: defaultTab },
    period: { defaultValue: ReportingPeriodFilter.PREVIOUS_MONTH },
  });

  const { tab: currentTab, period: currentPeriod } = values;
  const { tab: setTab, period: setPeriod } = setters;

  // Generate period picker options and value
  const periodFilterOptions = getTimeFilterOptions(
    ReportingPeriodFilter,
    'reporting.insightsCenter.filters.date.options'
  );
  const periodFilterValue = getDateFilterValue(
    currentPeriod,
    ReportingPeriodFilter,
    'reporting.insightsCenter.filters.date.options'
  );

  const handleChangePeriodFilter = (value: SingleValue<PeriodOption<ReportingPeriodFilter>>) => {
    if (value) {
      setPeriod(value.value);
    }
  };

  const tabs: TabsProps | undefined = useMemo(() => {
    return availableTabs.length <= 1
      ? undefined
      : {
          items: availableTabs,
          value: currentTab,
          onChange: (_event, newValue) => {
            setTab(newValue as string);
          },
        };
  }, [availableTabs, currentTab, setTab]);

  const renderTabContent = () => {
    // If no tabs are available, show a message
    if (availableTabs.length === 0) {
      return <Box sx={{ textAlign: 'center', py: 4 }}>{t('reporting.insightsCenter.noAccess')}</Box>;
    }

    // If current tab is not in available tabs, default to first available
    const validTab = availableTabs.find((tab) => tab.value === currentTab)?.value || availableTabs[0].value;

    if (validTab === 'training') {
      return <Training />;
    }

    return <Policy />;
  };

  return (
    <CommonLayout
      noPadding
      tabs={tabs}
      pageTitle={t('reporting.navItems.insightsCenter')}
      headerProps={{
        title: (
          <ReportingPeriodPicker
            value={periodFilterValue}
            options={periodFilterOptions}
            onChange={handleChangePeriodFilter}
            nameKey='reporting.insightsCenter.filters.date.name'
            placeholderKey='reporting.insightsCenter.filters.date.placeholder'
            size='small'
          />
        ),
      }}
    >
      <Box sx={{ px: 8, py: 4, backgroundColor: neutral[50] }}>{renderTabContent()}</Box>
    </CommonLayout>
  );
};

const InsightsCenter: React.FC = () => {
  return <InsightsCenterContent />;
};

export default InsightsCenter;
